import {
  MachineMetricsComponent,
  MachineMetricsType,
  MetricWithNamedType,
  allViewTypes
} from './MachineMetricsComponent';
import React, { useEffect, useState } from 'react';
import {
  filterMetricsByViewType,
  getMetricsByMachineAndViewType
} from './getMetricsByMachineAndViewType';
import { useDispatch, useSelector } from 'react-redux';

import AppServices from '_clean-code/services';
import { IMachineUIMetricsConfigService } from '_clean-code/services/contracts/machineUIMetricsConfig.service';
import { Link } from 'react-router-dom';
import { PageHeaderCustom } from 'containers/machine-settings/components/PageHeader';
import { PulsarIcon } from '@pulsarml/ui.react.components';
import ServiceContext from '_clean-code/services/context/service.context';
import { Translate } from '@utils/i18n';
import { getCompanyUIConfig } from 'ducks/config/actions';
import { getMachinesSelector } from 'ducks/machines/selectors';
import styles from './MachineUIConfigScreen.module.scss';

export interface MachineUIConfigScreenProps {
  machineId: number;
}

export const MachineUIConfigScreen = (
  props: MachineUIConfigScreenProps
): JSX.Element => {
  const machines = useSelector(getMachinesSelector);
  const dispatch = useDispatch();
  const [metricsCatalogueByViewType, setMetricsCatalogueByViewType] = useState<
    MachineMetricsType<MetricWithNamedType>
  >({});
  const data = useSelector((store: any) => store.config.data);
  const [machineName, setMachineName] = useState<string>('');
  const [updateId, setUpdateId] = useState<number>(0);
  const [machineMetrics, setMachineMetrics] = useState<
    MachineMetricsType<MetricWithNamedType>
  >({});
  const [
    availableMachineMetricsPerViewType,
    setAvailableMachineMetricsPerViewType
  ] = useState<Record<'desktopNonKpi' | 'tvKpi', string[]>>({
    desktopNonKpi: [],
    tvKpi: []
  });

  useEffect(() => {
    const machine = machines.find(
      (machine) => machine.id.toString() === props.machineId
    );
    if (machine) {
      setMachineName(machine.title);
    }
  }, [machines, props.machineId]);
  const saveUiMachineMetricConfig = async (
    val: MachineMetricsType<MetricWithNamedType>
  ) => {
    const service = ServiceContext.resolve<IMachineUIMetricsConfigService>(
      AppServices.MachineUIMetricsConfigService
    );
    await service.saveMachineUIMetricsConfig(props.machineId, val);
    dispatch(getCompanyUIConfig());
  };
  const fetchMachineUIMetricsConfig = React.useCallback(async () => {
    const service = ServiceContext.resolve<IMachineUIMetricsConfigService>(
      AppServices.MachineUIMetricsConfigService
    );
    const data = await service.getMachineUIMetricsConfig(
      props.machineId.toString()
    );

    setAvailableMachineMetricsPerViewType(data);
  }, [props.machineId]);

  useEffect(() => {
    dispatch(getCompanyUIConfig());
    props.machineId && fetchMachineUIMetricsConfig();
  }, [dispatch, fetchMachineUIMetricsConfig, props.machineId]);

  useEffect(() => {
    if (data) {
      const { machineMetricConfig, machineMetricCatalog } = data;
      if (!machineMetricConfig || !machineMetricCatalog) {
        return;
      }
      if (!machineMetricConfig[props.machineId]) {
        return;
      }

      const thisMachineMetricCatalog = allViewTypes.reduce((acc, viewType) => {
        acc[viewType] = getMetricsByMachineAndViewType(
          props.machineId,
          viewType,
          machineMetricCatalog
        );
        return acc;
      }, {});

      setMetricsCatalogueByViewType(
        filterMetricsByViewType(
          thisMachineMetricCatalog,
          availableMachineMetricsPerViewType
        )
      );
      setMachineMetrics(machineMetricConfig[props.machineId]);
    }
  }, [data, props.machineId, availableMachineMetricsPerViewType]);

  const executeWithDelay = (
    delay,
    viewType,
    metrics: MetricWithNamedType[]
  ) => {
    setMachineMetrics((value) => ({
      ...machineMetrics,
      ...value,
      [viewType]: metrics
    }));
    clearTimeout(updateId);
    const id = setTimeout(
      () =>
        saveUiMachineMetricConfig({
          ...machineMetrics,
          [viewType]: metrics
        }),
      delay
    );
    setUpdateId(Number(id.toString()));
  };
  const isViewTypeEnable = (viewType) =>
    viewType === 'tvKpi'
      ? data.realtime.singleMachineModeTvVersionEnabled
      : true; // desktopNonKpi
  return (
    <div className={styles.root}>
      <PageHeaderCustom>
        <>
          <Link to="/machines" className={styles.backButton}>
            <PulsarIcon
              name="left-arrow"
              width={14}
              color="#a3a3a5"
              className={styles.backButtonIcon}
            />
            <Translate value="back_to_realtime" />
          </Link>
          <p>
            {machineName}
            {' - '}
            <Translate value="machine_ui_config" />
          </p>
        </>
      </PageHeaderCustom>
      <MachineMetricsComponent
        isViewTypeEnable={isViewTypeEnable}
        machineMetrics={machineMetrics}
        metricsCatalogueByViewType={metricsCatalogueByViewType}
        onChange={(viewType, newData) => {
          executeWithDelay(500, viewType, newData);
        }}
      />
    </div>
  );
};
